import i18n from "../../../../../i18n";

function untitledLabel() {
  return i18n.t("wizardUntitled");
}

export function makeTempId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function getMenuDetailRawValue(menuDetails, key, language) {
  const found = Array.isArray(menuDetails)
    ? menuDetails.find((d) => d?.language === language)
    : null;
  return found?.[key] ?? "";
}

export function getMenuDetailValue(menuDetails, key, language) {
  return getMenuDetailRawValue(menuDetails, key, language).trim();
}

export function getPrimaryMenuLanguage(menu) {
  return menu?.primaryLanguage || "en";
}

export function getPrimaryMenuName(menu) {
  const lang = getPrimaryMenuLanguage(menu);
  return getMenuDetailValue(menu?.menuDetails, "menuName", lang);
}

export function getMenuOptionalLanguages(menu) {
  const primary = getPrimaryMenuLanguage(menu);
  return (Array.isArray(menu?.optionalLanguages) ? menu.optionalLanguages : []).filter(
    (code) => code && code !== primary
  );
}

export function getLocalizedDetailValue(details, key, language) {
  const found = Array.isArray(details)
    ? details.find((d) => d?.language === language)
    : null;
  return (found?.[key] || "").trim();
}

export function buildLocalizedDetails({
  primaryLanguage,
  optionalLanguages = [],
  titles = {},
  descriptions = {},
}) {
  const languages = [
    primaryLanguage,
    ...optionalLanguages.filter((code) => code && code !== primaryLanguage),
  ];
  const seen = new Set();

  return languages
    .filter((language) => {
      if (!language || seen.has(language)) return false;
      seen.add(language);
      return true;
    })
    .map((language) => ({
      language,
      productTitle: String(titles[language] || "").trim(),
      productDescription: String(descriptions[language] || "").trim(),
    }))
    .filter(
      (entry) =>
        entry.language === primaryLanguage ||
        entry.productTitle ||
        entry.productDescription
    );
}

export function buildCategoryLocalizedDetails({
  primaryLanguage,
  optionalLanguages = [],
  titles = {},
}) {
  const languages = [
    primaryLanguage,
    ...optionalLanguages.filter((code) => code && code !== primaryLanguage),
  ];
  const seen = new Set();

  return languages
    .filter((language) => {
      if (!language || seen.has(language)) return false;
      seen.add(language);
      return true;
    })
    .map((language) => ({
      language,
      categoryTitle: String(titles[language] || "").trim(),
      categoryDescription: "",
    }))
    .filter(
      (entry) => entry.language === primaryLanguage || entry.categoryTitle
    );
}

export function buildSubcategoryLocalizedDetails({
  primaryLanguage,
  optionalLanguages = [],
  titles = {},
}) {
  const languages = [
    primaryLanguage,
    ...optionalLanguages.filter((code) => code && code !== primaryLanguage),
  ];
  const seen = new Set();

  return languages
    .filter((language) => {
      if (!language || seen.has(language)) return false;
      seen.add(language);
      return true;
    })
    .map((language) => ({
      language,
      title: String(titles[language] || "").trim(),
      description: "",
    }))
    .filter((entry) => entry.language === primaryLanguage || entry.title);
}

export function getProductDisplayTitle(product, fallbackLanguage = "en") {
  const primaryLanguage = product?.primaryLanguage || fallbackLanguage;
  return (
    getLocalizedDetailValue(product?.productDetails, "productTitle", primaryLanguage) ||
    getLocalizedDetailValue(product?.productDetails, "productTitle", "en") ||
    (Array.isArray(product?.productDetails)
      ? product.productDetails.find((d) => d?.productTitle?.trim())?.productTitle?.trim()
      : "") ||
    untitledLabel()
  );
}

export function getProductDisplayDescription(product, fallbackLanguage = "en") {
  const primaryLanguage = product?.primaryLanguage || fallbackLanguage;
  return (
    getLocalizedDetailValue(product?.productDetails, "productDescription", primaryLanguage) ||
    getLocalizedDetailValue(product?.productDetails, "productDescription", "en") ||
    (Array.isArray(product?.productDetails)
      ? product.productDetails.find((d) => d?.productDescription?.trim())?.productDescription?.trim()
      : "") ||
    ""
  );
}

export function getCategoryDisplayTitle(category, language = "en") {
  return (
    getLocalizedDetailValue(category?.categoryDetails, "categoryTitle", language) ||
    getLocalizedDetailValue(category?.categoryDetails, "categoryTitle", "en") ||
    (Array.isArray(category?.categoryDetails)
      ? category.categoryDetails.find((d) => d?.categoryTitle?.trim())?.categoryTitle?.trim()
      : "") ||
    i18n.t("wizardPreviewUntitledCategory")
  );
}

export function getSubcategoryDisplayTitle(subcategory, language = "en") {
  return (
    getLocalizedDetailValue(subcategory?.subCategoryDetails, "title", language) ||
    getLocalizedDetailValue(subcategory?.subCategoryDetails, "title", "en") ||
    (Array.isArray(subcategory?.subCategoryDetails)
      ? subcategory.subCategoryDetails.find((d) => d?.title?.trim())?.title?.trim()
      : "") ||
    i18n.t("wizardPreviewUntitledSubcategory")
  );
}

export function categoryToFormState(category) {
  const titles = {};
  (category?.categoryDetails || []).forEach((detail) => {
    if (!detail?.language) return;
    titles[detail.language] = detail.categoryTitle || "";
  });
  return { titles };
}

export function subcategoryToFormState(subcategory) {
  const titles = {};
  (subcategory?.subCategoryDetails || []).forEach((detail) => {
    if (!detail?.language) return;
    titles[detail.language] = detail.title || "";
  });
  return {
    titles,
    bannerPreview: subcategory?.bannerUrl || null,
    bannerImageDisabled: subcategory?.bannerImageDisabled === true,
  };
}

export function reorderIdList(list, sourceId, targetId) {
  const order = Array.isArray(list) ? [...list] : [];
  const from = order.indexOf(sourceId);
  const to = order.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return order;
  order.splice(from, 1);
  order.splice(to, 0, sourceId);
  return order;
}

export function buildChoiceLocalizedDetails({
  primaryLanguage,
  optionalLanguages = [],
  titles = {},
}) {
  const languages = [
    primaryLanguage,
    ...optionalLanguages.filter((code) => code && code !== primaryLanguage),
  ];
  const seen = new Set();

  return languages
    .filter((language) => {
      if (!language || seen.has(language)) return false;
      seen.add(language);
      return true;
    })
    .map((language) => ({
      language,
      choiceTitle: String(titles[language] || "").trim(),
    }))
    .filter((entry) => entry.language === primaryLanguage || entry.choiceTitle);
}

export function buildItemLocalizedDetails({
  primaryLanguage,
  optionalLanguages = [],
  titles = {},
}) {
  const languages = [
    primaryLanguage,
    ...optionalLanguages.filter((code) => code && code !== primaryLanguage),
  ];
  const seen = new Set();

  return languages
    .filter((language) => {
      if (!language || seen.has(language)) return false;
      seen.add(language);
      return true;
    })
    .map((language) => ({
      language,
      itemTitle: String(titles[language] || "").trim(),
    }))
    .filter((entry) => entry.language === primaryLanguage || entry.itemTitle);
}

export function getChoiceDisplayTitle(choice, language = "en") {
  return (
    getLocalizedDetailValue(choice?.choiceDetails, "choiceTitle", language) ||
    getLocalizedDetailValue(choice?.choiceDetails, "choiceTitle", "en") ||
    (Array.isArray(choice?.choiceDetails)
      ? choice.choiceDetails.find((d) => d?.choiceTitle?.trim())?.choiceTitle?.trim()
      : "") ||
    untitledLabel()
  );
}

export function getItemDisplayTitle(item, language = "en") {
  return (
    getLocalizedDetailValue(item?.itemDetails, "itemTitle", language) ||
    getLocalizedDetailValue(item?.itemDetails, "itemTitle", "en") ||
    (Array.isArray(item?.itemDetails)
      ? item.itemDetails.find((d) => d?.itemTitle?.trim())?.itemTitle?.trim()
      : "") ||
    untitledLabel()
  );
}
